import {
  AccessibleContent,
  Alert,
  Badge,
  Button,
  Flex,
  Grid,
  Heading,
  IconAssignmentLine,
  IconButton,
  IconCoursesLine,
  IconDocumentLine,
  IconReviewScreenLine,
  IconTimerLine,
  List,
  Pill,
  Spinner,
  Text,
  ToggleDetails,
  Tooltip,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router";
import { AssignmentService } from "../../../services/AssignmentService";
import { ShareCodeService } from "../../../services/ShareCodeService";
import { TermsContext } from "../../../utils/Context";
import { sort } from "../../../utils/utils";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { GoalsSingleSelect } from "../../Selectors/GoalSelect";
import { ObjectiveSelect } from "../../Selectors/ObjectiveSelect";
import buildGoalsList from "./GoalsList";
import JAEvidence from "./JAEvidence";
import "./courseCard.css";

export const APDashboard = ({
  userDetails,
  selectedAssessmentPlan,
  cycles,
  selectedGoal,
  setSelectedGoal,
  dashboardFilters,
  isLoading,
  setIsLoading,
}) => {
  const { t } = useTranslation();
  const { canvasTerms, insightsTerms } = useContext(TermsContext);
  const showNotification = useNotification();
  const navigate = useNavigate();
  const [toggleDetails, setToggleDetails] = useState({
    open: false,
    offeringsOpen: true,
    type: "",
  });
  const [offeringList, setOfferingList] = useState({});
  const [filteredOfferingList, setFilteredOfferingList] = useState({});
  const [cycleName, setCycleName] = useState("");
  const [apTerms, setApTerms] = useState([]);
  const [apPrograms, setApPrograms] = useState([]);
  const [goalsList, setGoalsList] = useState([]);
  const [selectedObjective, setSelectedObjective] = useState(0);
  const [triggerObjectives, setTriggerObjectives] = useState(false);
  // const [numReflections, setNumReflections] = useState(0);
  // const [numActions, setNumActions] = useState(0);
  const [numJAEvidence, setNumJAEvidence] = useState(0);
  const [cycle, setCycle] = useState({});
  const [noAssignments, setNoAssignments] = useState(false);
  const [assignmentsByObjective, setAssignmentsByObjective] = useState({});
  const [loadingResults, setLoadingResults] = useState(false);

  const handleGoalsAndObjectives = (assessment_plan) => {
    const goalsList = buildGoalsList(assessment_plan);
    setGoalsList(goalsList);
    return goalsList;
  };

  const addToOfferingList = (assessment, offering, evidence) => {
    let termName = "";
    const evidence_list = [];
    evidence["goal"] = assessment.goal?.description ?? "";
    evidence_list.push(evidence);

    canvasTerms.forEach((term) => {
      if (term.id === evidence.term_id) {
        const insightsTerm = insightsTerms?.find(
          (t) => t.id === term.insights_term_id
        );
        if (!insightsTerm) {
          throw new Error(`${t("dashboard.missingInsightsTerm")} ${term.name}`);
        }
        termName = insightsTerm.name;
      }
    });
    if (!assessment.objective?.id) {
      assessment.objective = {
        id: "0",
        description: t("dashboard.noObjectiveAssociated"),
      };
    }
    const goal = [
      {
        id: assessment.goal?.id ?? "",
        description: assessment.goal?.description ?? "",
        objectives: [
          {
            id: assessment.objective.id,
            description: assessment.objective.description,
          },
        ],
      },
    ];

    const entry = {
      academic_subject: offering.course_summary.academic_subject,
      code: offering.code,
      name: `${offering.code} : ${termName}`,
      id: offering.id,
      numAssignments: 1,
      term: termName,
      goals: goal,
      parent_course_id: offering.course_summary.parent_course_id,
    };
    entry["evidence"] = evidence_list;

    return entry;
  };

  const saveSessionStorage = (state_to_save) => {
    localStorage.setItem("courseState", JSON.stringify(state_to_save));
  };

  const addToOfferingGoals = (current_goals, new_goal, new_objective) => {
    let goalFound = false;
    let i = 0;
    for (const goal of current_goals) {
      if (goal.id === new_goal.id) {
        goalFound = true;
        for (const objective of goal.objectives) {
          if (objective.id !== new_objective.id) {
            current_goals[i].objectives.push(new_objective);
          }
        }
      }
      if (goalFound) {
        break;
      }
      i = i + 1;
    }
    if (!goalFound) {
      current_goals.push({
        id: new_goal.id,
        description: new_goal.description,
        objectives: [
          { id: new_objective.id, description: new_objective.description },
        ],
      });
    }
    return current_goals;
  };

  useEffect(() => {
    if (selectedAssessmentPlan && canvasTerms.length !== 0) {
      if (selectedAssessmentPlan.include_goals_objs) {
        handleGoalsAndObjectives(selectedAssessmentPlan);
      }
      const termSet = new Set();
      const programSet = new Set();
      let collecting_evidence = 0;
      selectedAssessmentPlan.sections.forEach((section) => {
        section.assessments.forEach((assessment) => {
          if (assessment.collecting_evidence) {
            collecting_evidence += 1;
          }
          assessment.terms.forEach((term) => {
            termSet.add(term.insights_term_id);
          });
          if (assessment.programs) {
            assessment.programs.forEach((program) => {
              programSet.add(program.degreeName);
            });
          }
        });
      });
      setNumJAEvidence(collecting_evidence);
      setApTerms(insightsTerms.filter((term) => termSet.has(term.id)));
      setApPrograms(programSet);
      setCycle(
        cycles.find((cycle) => cycle.id === selectedAssessmentPlan.cycle.id)
      );
      setCycleName(selectedAssessmentPlan.cycle.name);
      AssignmentService.getAssignmentsByPlanId(selectedAssessmentPlan.id)
        .then((evidence) => {
          const tempOfferingList = {};
          evidence.forEach((assignment_evidence) => {
            let addedToOffering = false;
            for (const section of selectedAssessmentPlan.sections) {
              const assessment = section.assessments.find(
                (a) => a.id === assignment_evidence.assessment_id
              );
              if (assessment) {
                if (selectedAssessmentPlan.include_goals_objs) {
                  if (assessment.objective.id in assignmentsByObjective) {
                    assignmentsByObjective[assessment.objective.id] = [
                      ...assignmentsByObjective[assessment.objective.id],
                      assignment_evidence.id,
                    ];
                  } else {
                    assignmentsByObjective[assessment.objective.id] = [
                      assignment_evidence.id,
                    ];
                  }
                  setAssignmentsByObjective({ ...assignmentsByObjective });
                }
                const offering = assessment.offerings.find(
                  (o) => o.id === assignment_evidence.ddl_course_id
                );
                if (offering) {
                  addedToOffering = true;
                  // external assignments have -1 as course_id (ie canvas offering id). Build ID for them on basis of section id or on offering + term id combo (last is legacy support)
                  // where ddl_course_id now stores an offering id
                  const offering_key =
                    assignment_evidence.course_id !== -1
                      ? assignment_evidence.course_id
                      : assignment_evidence.ddl_course_id +
                        assignment_evidence.term_id +
                        (assignment_evidence.ddl_section_id ?? "");
                  if (userDetails.isPC()) {
                    if (
                      userDetails.ac_subjects.includes(
                        offering.course_summary.academic_subject.title
                      ) ||
                      (assessment.assessment_type === "external" &&
                        assessment.assessors.includes(userDetails.email))
                    ) {
                      if (Object.hasOwn(tempOfferingList, offering_key)) {
                        tempOfferingList[offering_key].numAssignments =
                          tempOfferingList[offering_key].numAssignments + 1;
                        assignment_evidence["goal"] =
                          assessment.goal?.description ?? "";
                        tempOfferingList[offering_key].evidence.push(
                          assignment_evidence
                        );
                        tempOfferingList[offering_key].goals =
                          addToOfferingGoals(
                            tempOfferingList[offering_key].goals,
                            assessment.goal ?? { id: "", description: "" },
                            assessment.objective
                          );
                      } else {
                        tempOfferingList[offering_key] = addToOfferingList(
                          assessment,
                          offering,
                          assignment_evidence
                        );
                      }
                    }
                  } else {
                    if (Object.hasOwn(tempOfferingList, offering_key)) {
                      tempOfferingList[offering_key].numAssignments =
                        tempOfferingList[offering_key].numAssignments + 1;
                      assignment_evidence["goal"] =
                        assessment.goal?.description ?? "";
                      tempOfferingList[offering_key].evidence.push(
                        assignment_evidence
                      );
                      tempOfferingList[offering_key].goals = addToOfferingGoals(
                        tempOfferingList[offering_key].goals,
                        assessment.goal ?? { id: "", description: "" },
                        assessment.objective
                      );
                    } else {
                      tempOfferingList[offering_key] = addToOfferingList(
                        assessment,
                        offering,
                        assignment_evidence
                      );
                    }
                  }
                }
              }
              if (addedToOffering) {
                break;
              }
            }
          });
          if (Object.keys(tempOfferingList).length > 0) {
            setNoAssignments(false);
          } else {
            setNoAssignments(true);
          }
          setOfferingList(tempOfferingList);
          setSelectedGoal({});
          setSelectedObjective(0);
          setTriggerObjectives(true);
          setIsLoading(false);
        })
        .catch((e) => {
          showNotification(
            `${t("notifications.general.fetchError", {
              obj: t("assignment.assignments"),
            })} - ${e.message}`,
            ERROR
          );
        });
    }
  }, [canvasTerms, selectedAssessmentPlan, showNotification]);

  useEffect(() => {
    if (Object.keys(selectedGoal).length === 0 && selectedObjective === 0) {
      setFilteredOfferingList(offeringList);
    } else {
      const tempOfferingList = {};
      Object.keys(offeringList).forEach((offering) => {
        offeringList[offering].goals.forEach((goal) => {
          if (goal.id === selectedGoal) {
            if (selectedObjective === 0) {
              tempOfferingList[offering] = offeringList[offering];
            } else {
              goal.objectives.forEach((objective) => {
                if (
                  (parseInt(selectedObjective) === 0 && objective.id === 0) ||
                  objective.id === selectedObjective
                ) {
                  tempOfferingList[offering] = offeringList[offering];
                }
              });
            }
          }
        });
      });
      setFilteredOfferingList(tempOfferingList);
    }
  }, [selectedGoal, selectedObjective, offeringList]);

  const onGoalSelect = (goalSelected) => {
    setSelectedGoal(goalSelected);
    setSelectedObjective(0);
    setTriggerObjectives(true);
  };

  const onObjectiveSelect = (objectiveSelected) => {
    setSelectedObjective(objectiveSelected);
  };

  //hotfix: to deal with different data types for has_submitted_submissions
  // const checkSubmissions = (has_submitted_submissions) => {
  //   const boolVal = has_submitted_submissions.toString().toLowerCase();
  //   if (boolVal === "true") {
  //     return true;
  //   } else {
  //     return false;
  //   }
  // };

  const handleViewResults = async () => {
    setLoadingResults(true);
    try {
      const shareCode = await ShareCodeService.getPlanShareCode(
        selectedAssessmentPlan.id
      );
      const sharePayload = await ShareCodeService.getShareable(
        shareCode.private
      );
      const state = {
        state: {
          purl_id: sharePayload.payload.context.purl_id,
          share_id: shareCode.private,
          public_share_id: shareCode.public ? shareCode.public : null,
          plan_id: sharePayload.payload.context.plan_id,
          token: sharePayload.token,
          cycle: cycle,
        },
      };
      return navigate("/results", state);
    } catch {
      showNotification(t("notifications.shareCode.viewResultsError"), ERROR);
    } finally {
      setLoadingResults(false);
    }
  };

  // useEffect(() => {
  //   if (cycles.length !== 0 && Object.keys(cycle).length !== 0) {
  //     ReflectionService.getReflectionsForContext(cycle.id).then(
  //       (reflectionData) => {
  //         setNumReflections(reflectionData.length);
  //       }
  //     );
  //     ActionService.getActionsForContext(cycle.id).then((actions) => {
  //       setNumActions(actions.length);
  //     });
  //   }
  // }, [cycle]);

  return (
    <>
      {/* {loadedCycles && !isLoading && !selectedAssessmentPlan ? (
        <Alert variant="info">{t("dashboard.noCyclesOrAssessments")}</Alert>
      ) : isLoading ? (
        <Skeleton
          count={1}
          style={{
            height: 40,
            borderRadius: 5,
          }}
        />
      ) : ( */}
      {/* selectedAssessmentPlan && ( */}
      <Flex alignItems="start" wrap="wrap">
        <Flex.Item size="100%" margin="0 0 small 0">
          <Flex justifyItems="space-between">
            <Flex.Item shouldGrow shouldShrink margin="0 medium 0 0">
              {isLoading ? (
                <Skeleton count={1} style={{ height: 40, borderRadius: 5 }} />
              ) : (
                <Heading as="h3" level="h3">
                  <Text as="span">{t("dashboard.planForCycle")}</Text>
                  <Text as="span" weight="normal">
                    {cycleName}
                    <Link
                      to={`/results-report/${selectedAssessmentPlan.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="small"
                        margin="0 small"
                        renderIcon={IconDocumentLine}
                      >
                        {t("results.generateReport")}
                      </Button>
                    </Link>
                    <Button
                      size="small"
                      onClick={handleViewResults}
                      renderIcon={
                        loadingResults ? (
                          <Spinner
                            renderTitle={t("global.pleaseWait")}
                            size="x-small"
                          />
                        ) : (
                          IconReviewScreenLine
                        )
                      }
                      disabled={loadingResults}
                    >
                      {t("dashboard.viewResults")}
                    </Button>
                    <Link
                      to={`/assessment-status/${selectedAssessmentPlan.id}`}
                    >
                      <Button
                        size="small"
                        margin="0 0 0 small"
                        renderIcon={IconTimerLine}
                      >
                        {t("assessmentPlanningDashboard.trackCycleProgress")}
                      </Button>
                    </Link>
                  </Text>
                </Heading>
              )}
            </Flex.Item>
            <Flex.Item textAlign="end">
              <Flex alignItems="end">
                {isLoading ? (
                  <Skeleton
                    inline
                    count={2}
                    style={{
                      height: 40,
                      borderRadius: 5,
                      width: 200,
                      marginLeft: 25,
                    }}
                  />
                ) : (
                  selectedAssessmentPlan.include_goals_objs && (
                    <>
                      <Flex.Item padding="0 small 0 0">
                        {t("dashboard.filterBy")}
                      </Flex.Item>
                      <Flex.Item padding="0 small 0 0">
                        <GoalsSingleSelect
                          selectHandler={onGoalSelect}
                          goalsList={goalsList}
                          selectedGoal={selectedGoal}
                          size="small"
                        />
                      </Flex.Item>
                      <Flex.Item>
                        <ObjectiveSelect
                          size="small"
                          selectedObjective={selectedObjective}
                          selectedGoal={selectedGoal}
                          selectHandler={onObjectiveSelect}
                          trigger={triggerObjectives}
                          setTrigger={setTriggerObjectives}
                          dashboardGoals={goalsList}
                        />
                      </Flex.Item>
                    </>
                  )
                )}
              </Flex>
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item size="100%" margin="0 0 small 0">
          {isLoading ? (
            <Skeleton
              count={1}
              style={{ height: 40, borderRadius: 5, width: 200 }}
            />
          ) : (
            <div>
              {apPrograms && Array.from(apPrograms).length > 0 && (
                <Text as="div" size="small">
                  <Text
                    as="span"
                    size="small"
                    weight="bold"
                    margin="0 0 small 0"
                  >
                    {t("dashboard.assessingPrograms")}
                  </Text>
                  <Text margin="0 small 0 0">
                    {Array.from(apPrograms).join(", ")}
                  </Text>
                </Text>
              )}
              <Text as="div" size="small">
                <Text as="span" size="small" weight="bold">
                  {t("dashboard.duringTerms")}
                </Text>
                {Array.from(apTerms).length > 0
                  ? Array.from(apTerms).map((apTerm, key) => (
                      <Pill margin="0 small 0 0" key={key}>
                        {apTerm.name}
                      </Pill>
                    ))
                  : t("global.na")}
              </Text>
            </div>
          )}
        </Flex.Item>
        {isLoading ? (
          <Flex.Item width="100%">
            <Skeleton count={1} style={{ height: 110, borderRadius: 5 }} />
          </Flex.Item>
        ) : (
          selectedAssessmentPlan.include_goals_objs && (
            <Flex.Item width="100%">
              <Heading as="h4" level="h5">
                <Text as="span" weight="bold" size="small">
                  {t("dashboard.goalsAndObjectives")}
                </Text>
              </Heading>
              {goalsList.map((goal, key) => (
                <ToggleDetails
                  key={key}
                  size="small"
                  summary={
                    <Text as="div" size="small" weight="bold">
                      {t("dashboard.goal")}
                      <Text as="span" weight="normal" size="small">
                        {goal.description}
                      </Text>
                    </Text>
                  }
                >
                  <Text as="div" size="small" weight="bold">
                    {t("dashboard.objectives")}
                  </Text>
                  <List size="small">
                    {goal.objectives
                      .filter((obj) => obj.id !== 0)
                      .map((obj, key) => (
                        <List.Item key={key}>{obj.description}</List.Item>
                      ))}
                  </List>
                </ToggleDetails>
              ))}
            </Flex.Item>
          )
        )}

        <Flex.Item size="100%">
          <Flex margin="medium 0" justifyItems="start">
            {isLoading ? (
              <Skeleton
                inline
                count={2}
                style={{
                  height: 40,
                  borderRadius: 5,
                  width: 40,
                  marginRight: 25,
                }}
              />
            ) : (
              <>
                <Flex.Item margin="0 medium 0 0">
                  <Badge
                    as="div"
                    count={Object.keys(filteredOfferingList).length}
                    pulse={Object.keys(filteredOfferingList).length > 0}
                    formatOutput={function (formattedCount) {
                      return (
                        <AccessibleContent
                          alt={t("dashboard.offeringCount", {
                            count: formattedCount,
                          })}
                        >
                          {formattedCount}
                        </AccessibleContent>
                      );
                    }}
                  >
                    <Tooltip
                      renderTip={t("dashboard.showCourseSections")}
                      placement="bottom"
                      on={["hover", "focus"]}
                    >
                      <IconButton
                        onClick={() =>
                          setToggleDetails({
                            offeringsOpen: !toggleDetails.offeringsOpen,
                            type: "offerings",
                          })
                        }
                        renderIcon={IconCoursesLine}
                        screenReaderLabel={t("dashboard.showCourseSections")}
                        withBorder={false}
                        withBackground={false}
                        color={
                          toggleDetails.offeringsOpen ? "primary" : "secondary"
                        }
                      />
                    </Tooltip>
                  </Badge>
                </Flex.Item>
                {/* Actions and Reflections have been removed from ac/pc dashboard until we know how to roll them up.
                      This list could be generated from hundreds of courses with many offerings so we need a strategy 
                      to make this a useable feature. 
                  <Flex.Item margin="0 medium 0 0">
                    <Badge
                      as="div"
                      count={numReflections}
                      pulse={numReflections !== 0}
                      formatOutput={function (formattedCount) {
                        return (
                          <AccessibleContent
                            alt={`You have ${formattedCount} reflections`}
                          >
                            {formattedCount}
                          </AccessibleContent>
                        );
                      }}
                    >
                      <Tooltip
                        renderTip="Show Reflections"
                        placement="bottom"
                        on={["hover", "focus"]}
                      >
                        <IconButton
                          onClick={() =>
                            setToggleDetails({
                              open:
                                toggleDetails.type === "reflections"
                                  ? !toggleDetails.open
                                  : true,
                              coursesOpen: false,
                              type: "reflections",
                            })
                          }
                          renderIcon={IconAnnotateLine}
                          screenReaderLabel="Show Reflections"
                          withBorder={false}
                          withBackground={false}
                          color={
                            toggleDetails.open &&
                            toggleDetails.type === "reflections"
                              ? "primary"
                              : "secondary"
                          }
                        />
                      </Tooltip>
                    </Badge>
                  </Flex.Item> */}
                {/* <Flex.Item margin="0 medium 0 0">
                    <Badge
                      as="div"
                      count={numActions}
                      pulse={numActions !== 0}
                      formatOutput={function (formattedCount) {
                        return (
                          <AccessibleContent
                            alt={`You have ${formattedCount} actions to review`}
                          >
                            {formattedCount}
                          </AccessibleContent>
                        );
                      }}
                    >
                      <Tooltip
                        renderTip="Show Actions"
                        placement="bottom"
                        on={["hover", "focus"]}
                      >
                        <IconButton
                          onClick={() =>
                            setToggleDetails({
                              open:
                                toggleDetails.type === "actions"
                                  ? !toggleDetails.open
                                  : true,
                              coursesOpen: false,
                              type: "actions",
                            })
                          }
                          renderIcon={IconBulletListCircleOutlineLine}
                          screenReaderLabel="Edits"
                          withBorder={false}
                          withBackground={false}
                          color={
                            toggleDetails.open &&
                            toggleDetails.type === "actions"
                              ? "primary"
                              : "secondary"
                          }
                        />
                      </Tooltip>
                    </Badge> */}
                {/* </Flex.Item> */}
                <Flex.Item>
                  <Badge
                    as="div"
                    count={numJAEvidence}
                    pulse={numJAEvidence !== 0}
                    formatOutput={function (formattedCount) {
                      return (
                        <AccessibleContent
                          alt={t("dashboard.evidenceCount", {
                            count: formattedCount,
                          })}
                        >
                          {formattedCount}
                        </AccessibleContent>
                      );
                    }}
                  >
                    <Tooltip
                      renderTip={t("dashboard.showJaEvidence")}
                      placement="bottom"
                      on={["hover", "focus"]}
                    >
                      <IconButton
                        onClick={() =>
                          setToggleDetails({
                            open:
                              toggleDetails.type === "assessments"
                                ? !toggleDetails.open
                                : true,
                            offeringsOpen: false,
                            type: "assessments",
                          })
                        }
                        renderIcon={IconAssignmentLine}
                        screenReaderLabel="Edits"
                        withBorder={false}
                        withBackground={false}
                        color={
                          toggleDetails.open &&
                          toggleDetails.type === "assessments"
                            ? "primary"
                            : "secondary"
                        }
                      />
                    </Tooltip>
                  </Badge>
                </Flex.Item>
              </>
            )}
          </Flex>
        </Flex.Item>
      </Flex>
      <Grid>
        <Grid.Row>
          <Grid.Col>
            {toggleDetails.offeringsOpen && (
              <div>
                {isLoading ? (
                  <Skeleton
                    count={1}
                    style={{ height: 25, borderRadius: 5, width: 100 }}
                  />
                ) : (
                  selectedAssessmentPlan && (
                    <Heading as="h3" level="h4" margin="0 0 small 0">
                      {t("dashboard.offeringsWithAssignments")}
                    </Heading>
                  )
                )}

                {isLoading ? (
                  <Flex wrap="wrap">
                    <Flex.Item size="33%" margin="0 small 0 0">
                      <Skeleton
                        count={2}
                        style={{
                          height: 90,
                          borderRadius: 5,
                          marginBottom: 20,
                        }}
                      />
                    </Flex.Item>
                    <Flex.Item size="33%">
                      <Skeleton
                        count={2}
                        style={{
                          height: 90,
                          borderRadius: 5,
                          marginBottom: 20,
                        }}
                      />
                    </Flex.Item>
                  </Flex>
                ) : noAssignments ||
                  Object.keys(filteredOfferingList).length === 0 ? (
                  <Alert variant="warning">
                    {t("dashboard.noAssignments")}
                  </Alert>
                ) : (
                  <Flex wrap="wrap" alignItems="stretch">
                    {Object.keys(filteredOfferingList).map(
                      (offering_id, key) => {
                        const offering = filteredOfferingList[offering_id];
                        return (
                          <Flex.Item
                            margin="0 small medium 0"
                            width="33%"
                            key={key}
                          >
                            <Link
                              to="/course"
                              onClick={() => {
                                saveSessionStorage({
                                  offering,
                                  cycle,
                                  dashboardFilters,
                                  selectedAssessmentPlan,
                                });
                              }}
                              onAuxClick={() => {
                                saveSessionStorage({
                                  offering,
                                  cycle,
                                  dashboardFilters,
                                  selectedAssessmentPlan,
                                });
                              }}
                              onContextMenu={() => {
                                saveSessionStorage({
                                  offering,
                                  cycle,
                                  dashboardFilters,
                                  selectedAssessmentPlan,
                                });
                              }}
                              state={{
                                offering,
                                cycle,
                                dashboardFilters,
                                selectedAssessmentPlan,
                              }}
                              style={{
                                textDecoration: "none",
                                color: "#000",
                                height: "100%",
                                display: "block",
                              }}
                            >
                              <Card
                                style={{ padding: 0 }}
                                className="courseCardLink"
                              >
                                <Flex
                                  justifyItems="space-between"
                                  alignItems="start"
                                  wrap="wrap"
                                  padding="small"
                                >
                                  <Flex.Item shouldGrow>
                                    <Flex wrap="wrap">
                                      <Flex.Item>
                                        <Heading
                                          as="h4"
                                          level="h5"
                                          weight="bold"
                                        >
                                          <Text weight="bold" size="small">
                                            {offering.code}
                                          </Text>
                                        </Heading>
                                        <Text as="div" size="small">
                                          {offering.numAssignments}{" "}
                                          {offering.numAssignments === 1
                                            ? "Assignment"
                                            : "Assignments"}
                                        </Text>
                                      </Flex.Item>
                                    </Flex>
                                  </Flex.Item>
                                  <Flex.Item textAlign="end">
                                    <Text as="div" size="x-small">
                                      {t("curriculumMap.term")}:{" "}
                                      <Pill>{offering.term}</Pill>
                                    </Text>

                                    {offering.evidence[0].assessor_ids.length >
                                      0 && (
                                      <Text as="div" size="small">
                                        <Tooltip
                                          color="primary-inverse"
                                          renderTip={
                                            <View>
                                              {sort(
                                                offering.evidence[0]
                                                  .assessor_ids
                                              ).map((assessor, key) => (
                                                <Text
                                                  key={key}
                                                  as="div"
                                                  size="small"
                                                >
                                                  {assessor}
                                                </Text>
                                              ))}
                                            </View>
                                          }
                                        >
                                          <span
                                            style={{
                                              textDecoration: "underline",
                                            }}
                                          >
                                            {t("assessment.assessors")}
                                          </span>
                                        </Tooltip>
                                      </Text>
                                    )}
                                  </Flex.Item>
                                </Flex>
                              </Card>
                            </Link>
                          </Flex.Item>
                        );
                      }
                    )}
                  </Flex>
                )}
              </div>
            )}
            {/* {toggleDetails.open && toggleDetails.type === "actions" && (
                <Actions contextId={cycle.id} />
              )} */}
            {/* {toggleDetails.open && toggleDetails.type === "reflections" && (
                <ContextReflections
                  contextId={cycle.id}
                  contextName={cycle.name}
                />
              )} */}
            {toggleDetails.open &&
              toggleDetails.type === "assessments" &&
              numJAEvidence >= 0 && (
                <JAEvidence
                  assessmentPlan={selectedAssessmentPlan}
                  collectingEvidence={numJAEvidence}
                />
              )}
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </>
  );
};
